import React, { type MutableRefObject, useState } from "react";
import type { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import type {
  GridFilterModel,
  GridPaginationModel,
  GridRowId,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { useQueryClient } from "@tanstack/react-query";
import { useResourceQueries } from "src/framework/useResourceQueries.ts";
import { useDataProvider } from "src/system/useDataProvider.ts";
import { ResourceEnum } from "src/constants/constants.ts";
import type { GridEventListener } from "@mui/x-data-grid";
import { B2bGateOrderDataGroupingColumn } from "src/admin/lists/b2b-gate-order/components/B2bGateOrderDataGroupingColumn.tsx";
import { getColumns } from "src/admin/lists/b2b-gate-order/getColumns.tsx";
import type { B2bGateOrderRow } from "src/admin/lists/b2b-gate-order/list.types.ts";
import type { B2bGateSupplierOrderResource } from "@exonn/js-sdk";
import { prepareFilterModel } from "src/system/useResourceList/utils/prepareFilterModel.tsx";
import { useErpTranslation } from "src/system/useErpTranslation.ts";
import { prepareSortModel } from "src/system/useResourceList/utils/prepareSortModel.ts";

export const useB2bGateOrderGridProps = ({
  resource,
  locale,
  apiRef,
  onRowClick: onRowClickProp,
  expandRowOnRowClick,
  refreshIndex,
  appliedFilterModel,
  includes,
}: {
  resource: string;
  locale: string;
  expandRowOnRowClick: boolean;
  refreshIndex: number;
  apiRef: MutableRefObject<GridApiPremium>;
  onRowClick?: GridEventListener<"rowClick">;
  appliedFilterModel: GridFilterModel;
  includes: string[];
}) => {
  const [loadingMap] = useState<Record<GridRowId, boolean>>({});
  const [errorMap] = useState<Record<GridRowId, Error>>({});

  const queryClient = useQueryClient();
  const queries = useResourceQueries();
  const dataProvider = useDataProvider();
  const { t } = useErpTranslation({
    resource: ResourceEnum.B2bGateOrder,
    type: "list",
  });
  const fetchSubRows = async ({
    groupKeys,
    row,
    filterModel,
    paginationModel,
    sortModel,
  }: {
    groupKeys: string[];
    filterModel: GridFilterModel;
    paginationModel: GridPaginationModel;
    sortModel: GridSortModel;
    row: B2bGateOrderRow;
  }) => {
    const params = {
      groupKeys,
      filter: {
        ...filterModel,
        items: [
          ...filterModel.items,
          {
            field: "b2b_gate_order_id",
            operator: "is",
            value: groupKeys[0],
          },
        ],
      },
      withAggregation: true,
      useSimplePagination: false,
      include: ["supplier"],
      sort: sortModel,
      locale,
      page: {
        size: 99999,
        number: 1,
      },
      resource: ResourceEnum.B2bGateSupplierOrder,
    };
    const response = await queryClient.fetchQuery({
      queryKey: queries.list(params),
      queryFn: async () => {
        return await dataProvider.getList(params);
      },
    });

    const nextRows = response.data as B2bGateSupplierOrderResource[];
    const queryKey = queries.list({
      filter: prepareFilterModel(
        appliedFilterModel,
        getColumns({
          t,
        }),
      ),
      sort: prepareSortModel(
        sortModel,
        getColumns({
          t,
        }),
      ),
      refreshId: refreshIndex,
      include: includes,
      // todo: move "de" to constants
      locale: locale || "de",
      page: {
        size: paginationModel.pageSize,
        number: paginationModel.page + 1,
      },
      resource: ResourceEnum.B2bGateOrder,
    });
    queryClient.setQueryData(queryKey, (prevResponse: any) => {
      if (!prevResponse) {
        return prevResponse;
      }
      return {
        ...prevResponse,
        data: [...prevResponse.data, ...nextRows],
      };
    });
    return {
      rows: nextRows,
    };
  };
  if (resource === ResourceEnum.B2bGateOrder) {
    const handleRowClick: GridEventListener<"rowClick"> = (
      params,
      event,
      details,
    ) => {
      onRowClickProp?.(params, event, details);
    };
    return {
      groupingColDef: {
        headerName: t("table.columns.orderNumber"),
        hideable: false,
        width: 170,
        disableReorder: true,
        // @ts-ignore
        renderCell: (params) => {
          return (
            <B2bGateOrderDataGroupingColumn
              onFetchRows={fetchSubRows}
              loadingMap={loadingMap}
              errorMap={errorMap}
              {...params}
            />
          );
        },
      },
      onRowClick: handleRowClick,
      treeData: true,
      getTreeDataPath: (row: any) => row.path,
    };
  }

  return {};
};
